import { isProblem } from "@kodall/kodall-client/dist/types";
import { intercept } from "~/service/kodall-client";

export interface CompanySettings {
  key: number;
  denumire: string;
  email: string;
  telefon: string;
  footer: string;
  logo_base64?: string;
}

const defaultValues: CompanySettings = {
  key: -1,
  denumire: "TO BE DEFINED",
  email: "TO BE DEFINED",
  telefon: "TO BE DEFINED",
  footer: "TO BE DEFINED"
};

export const useCompanyData = () => {
  // Folosim useState pentru a păstra datele în memorie "global" în aplicație
  // Astfel, dacă le cerem în mai multe locuri, sunt aceleași date
  const companySettings = useState<CompanySettings>('companySettings', () => (defaultValues));
  const companyLogoUrl = useState<string | undefined>('companyLogoUrl', () => undefined);
  const companyLogoLoading = useState<boolean>('companyLogoLoading', () => false);

  const kc = useSecurity().kc.value;

  const fetchCompanySettingsBase = async (): Promise<CompanySettings[]> => {
    // Facem cererea către backend
    try {
      const res = await intercept<CompanySettings[]>(() =>
        kc.fetch<CompanySettings>(`
          FETCH p_app_config (key, denumire, email, telefon, footer) 
          LIMIT 1
        `)
      );

      if (res && res.length > 0) {
        const row = res[0]!;
        const normalize = (v?: string) =>
            v !== null && v!== undefined && v !== "" ? v : "TO BE DEFINED";
        
        const normalized: CompanySettings = {
          ...row,
          denumire: normalize(row.denumire),
          email: normalize(row.email),
          telefon: normalize(row.telefon),
          footer: normalize(row.footer),
        };

        companySettings.value = normalized;

        return [normalized];
      } else {
        // Fallback: Dacă serverul nu returnează nimic (tabelă goală),
        // folosim valori implicite locale ca să arate bine site-ul.
        console.warn("Backend p_app_config is empty. Using default local values.");
               
        companySettings.value = defaultValues;
        return [defaultValues];
      }
    } catch (e) {
      console.error("Failed to fetch company settings", e);
      // În caz de eroare de rețea, rămânem cu "TO BE DEFINED" sau putem pune default-urile și aici
    }
    
    return [];
  };

  const fetchCompanyLogo = async (): Promise<void> => {
    companyLogoLoading.value = true;

    try {
      const res = await intercept<CompanySettings[]>(() => 
        kc.fetch<CompanySettings>(`
          FETCH p_app_config (logo_base64)
          LIMIT 1
          `)
      );

      const raw = res?.[0]?.logo_base64?.trim();

      companyLogoUrl.value = raw ? raw : undefined;
    }
    catch (e) {
      console.error("Failed to fetch company logo", e);
      companyLogoUrl.value = undefined;
    }
    finally {
      companyLogoLoading.value = false;
    }
  }

  return {
    companySettings,
    companyLogoUrl,
    companyLogoLoading,
    fetchCompanySettingsBase,
    fetchCompanyLogo
  };
};
